import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIF_ROUTES = {
  time_request: '/admin/time-requests',
  new_booking: '/admin/calendar',
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/notifications`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch (e) {
      // silent fail
    }
  }, []);

  const connectWS = useCallback(async () => {
    try {
      const tokenRes = await axios.get(`${BACKEND_URL}/api/auth/ws-token`, { withCredentials: true });
      const token = tokenRes.data.token;
      const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + `/api/notifications/ws?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.event === 'connected') {
            // initial unread sync
            if (typeof msg.unread_count === 'number') setUnread(msg.unread_count);
          } else if (msg.event === 'notification' && msg.data) {
            const notif = msg.data;
            setNotifications((prev) => [notif, ...prev].slice(0, 50));
            setUnread((u) => u + 1);
            toast.info(notif.title, {
              description: notif.message,
              duration: 5000,
            });
          }
        } catch {
          // ignore non-JSON (e.g., pong)
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        // Auto-reconnect after 5s
        reconnectTimeoutRef.current = setTimeout(connectWS, 5000);
      };

      ws.onerror = () => {
        // Will trigger onclose
      };

      // Keep-alive ping every 25s
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 25000);
    } catch (e) {
      // Auth failed, retry in 10s
      reconnectTimeoutRef.current = setTimeout(connectWS, 10000);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    connectWS();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
    };
  }, [fetchNotifications, connectWS]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/notifications/read-all`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch {
      toast.error('Failed to mark all as read');
    }
  };

  const markOneRead = async (id) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => (n.notification_id === id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch {
      // ignore
    }
  };

  const handleNotifClick = (n) => {
    if (!n.is_read) markOneRead(n.notification_id);
    const route = NOTIF_ROUTES[n.type];
    if (route) { setOpen(false); navigate(route); }
  };

  const timeAgo = (iso) => {
    try {
      const d = new Date(iso);
      const diff = Math.floor((Date.now() - d.getTime()) / 1000);
      if (diff < 60) return `${diff}s ago`;
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return `${Math.floor(diff / 86400)}d ago`;
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative text-[#94A3B8] hover:text-white transition-colors p-1"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-[#F97316] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            data-testid="notification-unread-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white border border-[#E2E8F0] rounded-lg shadow-xl z-50 max-h-[480px] overflow-hidden flex flex-col"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between p-3 border-b border-[#F1F5F9]">
            <span className="font-bold text-sm text-[#0A0F1C]">Notifications</span>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#1D4ED8] hover:underline font-medium"
                data-testid="mark-all-read-btn"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#94A3B8]">No notifications yet</div>
            ) : (
              notifications.map((n, idx) => (
                <button
                  key={n.notification_id || idx}
                  onClick={() => handleNotifClick(n)}
                  className={`w-full text-left p-3 border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC] transition-colors ${
                    !n.is_read ? 'bg-[#EFF6FF]' : ''
                  }`}
                  data-testid={`notification-item-${idx}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#0A0F1C] truncate">{n.title}</div>
                      <div className="text-xs text-[#475569] line-clamp-2 mt-0.5">{n.message}</div>
                      <div className="text-[10px] text-[#94A3B8] mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                    {!n.is_read && <span className="w-2 h-2 bg-[#F97316] rounded-full mt-1.5 shrink-0"></span>}
                    {n.is_read && <Check size={12} className="text-[#94A3B8] mt-1.5 shrink-0" />}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
